/**
 * Analytics Provider Component
 * 
 * Wraps the application with PostHog provider and manages analytics state.
 * - Initializes PostHog with privacy-safe configuration
 * - Provides PostHog context to child components
 * - Handles user identification on auth state changes
 */

import { useEffect } from 'react';
import { PostHogProvider } from '@posthog/react';
import { useAuthStore } from '../store/auth';
import {
  posthog,
  initializePostHog,
  identifyUser,
  resetIdentity,
  hasAnalyticsConsent,
} from '../lib/posthog';
import CookieConsent from './CookieConsent';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

// Initialize PostHog once at module load
initializePostHog();

export default function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const { user, isAuthenticated } = useAuthStore();

  // Handle user identification when auth state changes
  useEffect(() => {
    if (!hasAnalyticsConsent()) {
      return;
    }

    if (isAuthenticated && user) {
      // Identify user with privacy-safe properties
      identifyUser(user.firebaseUid, {
        role: user.role,
        createdAt: user.createdAt,
      });
    } else {
      // Reset identity on logout
      resetIdentity();
    }
  }, [isAuthenticated, user]);

  return (
    <PostHogProvider client={posthog}>
      {children}
      <CookieConsent />
    </PostHogProvider>
  );
}
